import type { AutomationConfirmationMode, AutomationStatus } from "@lifter/hub-ui/browser-shared";
import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHubApi } from "./context";

export function useAutomationsQuery() {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.automations(),
    queryFn: () => api.automations.list(),
  });
}

export function useAutomationQuery(agentId: string, automationId: string) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.automation(agentId, automationId),
    queryFn: () => api.automations.get(agentId, automationId),
  });
}

export function useAutomationRunsQuery(agentId: string, automationId: string) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.automationRuns(agentId, automationId),
    queryFn: () => api.automations.runs(agentId, automationId),
  });
}

export function useAutomationRunQuery(agentId: string, automationId: string, runId: string) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.automationRun(agentId, automationId, runId),
    queryFn: () => api.automations.getRun(agentId, automationId, runId),
    // A run finishes on its own, with nothing to push the news. Polling stops
    // the moment it settles, so a page left open on old history is quiet.
    refetchInterval: (query) => {
      const status = query.state.data?.run.status;
      return status === "pending" || status === "running" ? 2000 : false;
    },
  });
}

/** Everything a change touches: the automation, the list it appears on, its runs. */
function useAutomationInvalidation(agentId: string, automationId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: hubQueryKeys.automation(agentId, automationId),
    });
    void queryClient.invalidateQueries({ queryKey: hubQueryKeys.automations(), exact: true });
    void queryClient.invalidateQueries({
      queryKey: hubQueryKeys.automationRuns(agentId, automationId),
    });
  };
}

export function useUpdateAutomationMutation(
  agentId: string,
  automationId: string,
  feedback: { onError?(error: unknown): void; onSuccess?(): void } = {},
) {
  const api = useHubApi();
  const invalidate = useAutomationInvalidation(agentId, automationId);
  return useMutation({
    mutationFn: (patch: {
      status?: AutomationStatus;
      activeRevisionSeq?: number;
      confirmationMode?: AutomationConfirmationMode;
      schedule?: string | null;
    }) => api.automations.update(agentId, automationId, patch),
    onSuccess: () => {
      invalidate();
      feedback.onSuccess?.();
    },
    onError: feedback.onError,
  });
}

export function useRunAutomationMutation(
  agentId: string,
  automationId: string,
  feedback: { onError?(error: unknown): void; onSuccess?(runId: string | null): void } = {},
) {
  const api = useHubApi();
  const invalidate = useAutomationInvalidation(agentId, automationId);
  return useMutation({
    mutationFn: (args?: Record<string, unknown>) =>
      api.automations.run(agentId, automationId, args ? { args } : {}),
    onSuccess: (outcome) => {
      invalidate();
      feedback.onSuccess?.("run" in outcome ? outcome.run.id : null);
    },
    onError: feedback.onError,
  });
}

export function useCancelAutomationRunMutation(
  agentId: string,
  automationId: string,
  runId: string,
  feedback: { onError?(error: unknown): void; onSuccess?(cancelled: boolean): void } = {},
) {
  const api = useHubApi();
  const invalidate = useAutomationInvalidation(agentId, automationId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.automations.cancelRun(agentId, automationId, runId),
    onSuccess: ({ cancelled }) => {
      invalidate();
      void queryClient.invalidateQueries({
        queryKey: hubQueryKeys.automationRun(agentId, automationId, runId),
      });
      feedback.onSuccess?.(cancelled);
    },
    onError: feedback.onError,
  });
}
